import { Bell, Sparkles, Flame, ClipboardCheck, BookOpen, Target, Calendar, PartyPopper, Megaphone } from "lucide-react";
import { progress as progressApi, child as childApi, activities as activitiesApi, deepDive as deepDiveApi, notifications as notificationsApi } from "@/lib/api";
import {
  ADMIN_TYPE_VISUALS,
  DISMISSED_KEY,
  ICONS,
  readFirstSeen,
  readIds,
  writeFirstSeen,
} from "./utils";

/**
 * Build the full list of "things the parent should know" by joining
 * progress, child, daily-activities, focus-eligibility and the admin
 * inbox. Returns already-filtered (dismissed items dropped) + stable
 * `time` timestamps.
 *
 * Shared by <NotificationsBell/> (unread badge count) and the
 * standalone /notifications page.
 */
export async function buildDerivedNotifications({ userId } = {}) {
  const [s, c, dailyResp, ddElig, adminInbox] = await Promise.all([
    progressApi.streak().catch(() => null),
    childApi.get().catch(() => null),
    activitiesApi.daily().catch(() => null),
    deepDiveApi.eligibility().catch(() => null),
    notificationsApi.inbox().catch(() => []),
  ]);
  const dailyList = Array.isArray(dailyResp?.daily) ? dailyResp.daily : [];
  const nextIncomplete = dailyList.find((a) => !a.completed_today);
  const todayTarget = nextIncomplete ? `/activities/${nextIncomplete.id}` : "/activities";
  const nowIso = new Date().toISOString();
  const todayKey = nowIso.slice(0, 10);
  const next = [];

  if (Array.isArray(adminInbox)) {
    for (const a of adminInbox) {
      const visuals = ADMIN_TYPE_VISUALS[a.type] || ADMIN_TYPE_VISUALS.announcement;
      const fullTitle = a.emoji ? `${a.emoji} ${a.title}`.trim() : a.title;
      next.push({
        id: `admin-${a.id}`, adminId: a.id, category: "updates",
        icon: ICONS[visuals.iconKey] || Megaphone, iconKey: visuals.iconKey, tone: visuals.tone,
        title: fullTitle, body: a.message, cta: "Open",
        time: a.sent_at || nowIso, url: a.link || "/",
      });
    }
  }
  if (userId) {
    next.push({
      id: `welcome-${userId}`, category: "updates",
      icon: PartyPopper, iconKey: "PartyPopper", tone: "coral",
      title: "Welcome to Kiddo+ 🎉",
      body: "Here's how to read your child's first report — open it to see strengths, focus areas and your personalised daily plan.",
      cta: "Open your report", time: nowIso, url: "/report",
    });
  }
  if (c && dailyList.length > 0) {
    next.push({
      id: `daily-plan-${todayKey}`, category: "activities",
      icon: Calendar, iconKey: "Calendar", tone: "mint",
      title: "Today's activities are ready 🌱",
      body: nextIncomplete
        ? `${dailyList.length} activit${dailyList.length === 1 ? "y" : "ies"} planned — start with "${nextIncomplete.title}".`
        : `${dailyList.length} activit${dailyList.length === 1 ? "y" : "ies"} planned for today.`,
      cta: nextIncomplete ? "Start today's plan" : "View today's plan",
      time: nowIso, url: todayTarget,
    });
  }
  if (!c) {
    next.push({
      id: "needs-child", category: "reminders",
      icon: ClipboardCheck, iconKey: "ClipboardCheck", tone: "coral",
      title: "Add your child's profile",
      body: "Set up your child's details to unlock personalised daily activities.",
      cta: "Add child", time: nowIso, url: "/onboarding",
    });
  }
  if (c && s && s.unlock_assessment) {
    next.push({
      id: "reassessment-ready", category: "reminders",
      icon: Sparkles, iconKey: "Sparkles", tone: "mint",
      title: "Reassessment unlocked",
      body: "30 days of activities complete — time to retake the assessment and see how far you've come.",
      cta: "Start reassessment", time: nowIso, url: "/assessment",
    });
  }
  if (c && s && !s.unlock_assessment && s.days_remaining > 0 && s.days_remaining <= 5) {
    next.push({
      id: `reassessment-soon-${s.days_remaining}`, category: "reminders",
      icon: ClipboardCheck, iconKey: "ClipboardCheck", tone: "plum",
      title: `Reassessment in ${s.days_remaining} day${s.days_remaining === 1 ? "" : "s"}`,
      body: "You're almost at 30 days of activities completed! Keep the streak going to unlock your next reassessment.",
      cta: "View progress", time: nowIso, url: "/progress",
    });
  }
  if (ddElig?.is_pro && Array.isArray(ddElig.qualifying_domains)) {
    const completedKeys = new Set(ddElig.completed_domains || []);
    for (const d of ddElig.qualifying_domains) {
      if (completedKeys.has(d.domain_key) && d.last_completed_at && d.can_retake === true) {
        next.push({
          id: `focus-unlock-${d.domain_key}-${d.last_completed_at.slice(0, 10)}`, category: "reminders",
          icon: Target, iconKey: "Target", tone: "mint",
          title: `${d.icon || "🎯"} ${d.domain_name} re-take unlocked`,
          body: "You've logged 30 days of activities since your last Focus Areas check — re-take it in 2 min to see how the sub-skills have shifted.",
          cta: "Re-take Focus Areas", time: d.last_completed_at || nowIso, url: "/deep-dive",
        });
      }
    }
  }
  if (s && s.current_streak >= 3) {
    next.push({
      id: `streak-${s.current_streak}`, category: "activities",
      icon: Flame, iconKey: "Flame", tone: "coral",
      title: `${s.current_streak}-day streak 🔥`,
      body: nextIncomplete
        ? `Keep it alive — tap to start "${nextIncomplete.title}".`
        : "You're building consistency — log today's activities to keep it alive.",
      cta: nextIncomplete ? `Start ${nextIncomplete.emoji || ""} ${nextIncomplete.title}`.trim() : "Today's plan",
      time: nowIso, url: todayTarget,
    });
  }
  next.push({
    id: "tip-evergreen", category: "updates",
    icon: BookOpen, iconKey: "BookOpen", tone: "plum",
    title: "Read general guidelines",
    body: "A 2-minute primer on how to get the most out of each Kiddo+ activity.",
    cta: "Open guidelines", time: nowIso, url: "/activities?open=guidelines",
  });

  const dismissedIds = readIds(DISMISSED_KEY);
  const liveItems = next.filter((it) => !dismissedIds.has(it.id));
  const seen = readFirstSeen();
  let mutated = false;
  const withStableTimes = liveItems.map((it) => {
    if (!seen[it.id]) { seen[it.id] = it.time || nowIso; mutated = true; }
    return { ...it, time: seen[it.id] };
  });
  if (mutated) writeFirstSeen(seen);
  return withStableTimes;
}

export function categorize(items) {
  // Bell used to receive items without `category` (older cache) — fall
  // back to reasonable defaults so nothing silently disappears.
  const guess = (it) => it.category || (
    it.iconKey === "Calendar" || it.iconKey === "Flame" ? "activities"
    : it.iconKey === "ClipboardCheck" || it.iconKey === "Sparkles" || it.iconKey === "Target" ? "reminders"
    : "updates"
  );
  return items.reduce((acc, it) => {
    const k = guess(it);
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
}
